export const deriveFallbackOldPrice = ({
	unitPrice,
	baseUnitPrice,
	fallback
}: {
	unitPrice: number;
	baseUnitPrice: number;
	fallback: number | null;
}) => {
	if (unitPrice < baseUnitPrice) {
		return baseUnitPrice;
	}

	return fallback;
};

export const parseQty = (value: unknown): number | null => {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
};

export const calculateDiscountPercent = (
	oldPrice: number | null,
	price: number
) => {
	if (!oldPrice) {
		return 0;
	}

	return Math.max(0, Math.floor(((oldPrice - price) / oldPrice) * 100));
};

/**
 * Prices within this of each other are the same price, rounded from different
 * points — the basket line and the quantity breaks are two Norce calculations.
 * The same step `composables/useProductPrices.ts` uses; see docs/PRICING.md.
 */
export const PRICE_ROUNDING_STEP = 0.011;

/**
 * What one unit costs on its own: the `QtyBreak` 1 row, which is what the
 * product page strikes a multipack through against.
 *
 * Only that row. When Norce returns the real breaks alone (2, 4, …), the lowest
 * of them is a break price, and striking a line through against it understates
 * or hides the saving — so no saving is shown rather than a wrong one.
 */
export const singleUnitPriceFromBreaks = (
	breaks: {
		QtyBreak: unknown;
		ProductPrice?: { ValueIncVat?: number | null } | null;
	}[]
): number | null => {
	const prices = breaks
		.filter((breakItem) => parseQty(breakItem.QtyBreak) === 1)
		.map((breakItem) => breakItem.ProductPrice?.ValueIncVat)
		.filter((price): price is number => typeof price === "number" && price > 0);

	// Two rows for one unit: the lower price, so a saving is never overstated.
	return prices.length ? Math.min(...prices) : null;
};

/**
 * The unit price a basket line was reduced from, or `null` when it was not.
 *
 * Candidates: `PriceOriginal`, `PriceStandard`, and the single-unit price. A
 * quantity break shows up in neither of the first two — Norce prices the line
 * on the break, so all three line fields carry the break price.
 *
 * The highest wins, not the first. Every candidate is a price this product is
 * actually sold at, and a discount code on a break line leaves the break price
 * in `PriceOriginal`: taking the first would strike the line through against
 * the break price and lose the break saving the line still carries.
 */
export const resolveLineReferencePrice = ({
	unitPrice,
	original,
	standard,
	singleUnitPrice
}: {
	unitPrice: number | null | undefined;
	original: number | null | undefined;
	standard: number | null | undefined;
	singleUnitPrice: number | null | undefined;
}): number | null => {
	if (!unitPrice) {
		return null;
	}

	const reducedFrom = [original, standard, singleUnitPrice].filter(
		(candidate): candidate is number =>
			typeof candidate === "number" &&
			candidate - unitPrice >= PRICE_ROUNDING_STEP
	);

	return reducedFrom.length ? Math.max(...reducedFrom) : null;
};
